import React from "react";
import { useQuery } from "@tanstack/react-query";
import numeral from "numeral";
import { httpClient } from "common/httpClient";
import * as utils from "common/utils";
import { Panel, PanelBody } from "components/Panel";
import { SortableTable, SortableTableHeader } from "components/SortableTable";
import ActionLink from "components/ActionLink";
import Alert from "components/Alert";
import { EditOrgActionLink } from "containers/Admin/Orgs";

const money = (value) => numeral(value || 0).format("$0,0.00");

/**
 * How a single purchase is identified alongside its date: the transaction type, plus whichever
 * of the invoice number or payment reference it happens to carry.
 */
const purchaseLabel = (purchase) => {
    const type = (purchase.transactionType || "").replace(/([a-z])([A-Z])/g, "$1 $2");
    const identifier = purchase.invoiceNumber ? `invoice ${purchase.invoiceNumber}` : purchase.reference;
    return [type, identifier].filter((part) => part).join(" · ");
};

export default function DiscountsReport() {
    const { data: orgs = [], isLoading, error } = useDiscountedOrgs();
    const [expandedOrgId, setExpandedOrgId] = React.useState(null);

    const totalSpent = orgs.reduce((total, org) => total + (org.totalSpentExclGst || 0), 0);
    const totalDiscount = orgs.reduce((total, org) => total + (org.totalDiscountExclGst || 0), 0);
    const totalPurchases = orgs.reduce((total, org) => total + (org.discountedPurchaseCount || 0), 0);

    return (
        <Panel title="Discounts report" loading={isLoading} green>
            <PanelBody loading={isLoading}>
                {error && <Alert type="error" text={error.message} />}
                {!error && orgs.length === 0 && <Alert type="info" text="No organisations currently have a farm subscription discount." />}
                {!error && orgs.length > 0 && (
                    <>
                        <div className="Table u-mb-md">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Overview</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <div className="HeroValue">
                                                <div className="HeroValue-item">
                                                    <h5>Orgs with a discount</h5>
                                                    <p className="HeroValue-item-number">{orgs.length}</p>
                                                </div>
                                                <div className="HeroValue-item">
                                                    <h5>Discounted purchases</h5>
                                                    <p className="HeroValue-item-number">{totalPurchases}</p>
                                                </div>
                                                <div className="HeroValue-item">
                                                    <h5>Total spent (excl GST)</h5>
                                                    <p className="HeroValue-item-number">{money(totalSpent)}</p>
                                                </div>
                                                <div className="HeroValue-item">
                                                    <h5>Total discount (excl GST)</h5>
                                                    <p className="HeroValue-item-number">{money(totalDiscount)}</p>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <SortableTable data={orgs} defaultSortCriteria={{ fieldName: "totalDiscountExclGst", fieldType: "number", desc: true }}>
                            {({ data, sortCriteria, handleSort }) => (
                                <table>
                                    <thead>
                                        <tr>
                                            <SortableTableHeader label="Organisation" fieldName="accountName" sortCriteria={sortCriteria} handleSort={handleSort} />
                                            <SortableTableHeader label="Discount" fieldName="discountPercentage" sortCriteria={sortCriteria} handleSort={handleSort} number shrink />
                                            <SortableTableHeader label="Discounted purchases" fieldName="discountedPurchaseCount" sortCriteria={sortCriteria} handleSort={handleSort} number shrink />
                                            <SortableTableHeader label="Farms" fieldName="discountedFarmCount" sortCriteria={sortCriteria} handleSort={handleSort} number shrink />
                                            <SortableTableHeader label="Total spent (excl GST)" fieldName="totalSpentExclGst" sortCriteria={sortCriteria} handleSort={handleSort} number shrink />
                                            <SortableTableHeader label="Discount total (excl GST)" fieldName="totalDiscountExclGst" sortCriteria={sortCriteria} handleSort={handleSort} number shrink />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map((org) => {
                                            const purchases = org.purchases || [];
                                            const isExpanded = expandedOrgId === org.accountId;

                                            return (
                                                <React.Fragment key={org.accountId}>
                                                    <tr className="item">
                                                        <td>
                                                            <EditOrgActionLink org={{ id: org.accountId, name: org.accountName }} label={org.accountName} className="" />
                                                        </td>
                                                        <td className="u-textRight u-textNoWrap">
                                                            {purchases.length > 0 ? (
                                                                <ActionLink id={`toggle-purchases-${org.accountId}`} className="u-flex u-flexAlignItemsCenter u-flexJustifyEnd" title={isExpanded ? "Hide the purchases behind these totals" : "Show the purchases behind these totals, with the rate each was bought at"} onClick={() => setExpandedOrgId(isExpanded ? null : org.accountId)}>
                                                                    <span>{org.discountPercentage}%</span>
                                                                    <i className={`icon icon-arrow-${isExpanded ? "up" : "down"} u-ml-xs`} />
                                                                </ActionLink>
                                                            ) : (
                                                                <>{org.discountPercentage}%</>
                                                            )}
                                                        </td>
                                                        <td className="u-textRight u-textNoWrap">{org.discountedPurchaseCount}</td>
                                                        <td className="u-textRight u-textNoWrap">{org.discountedFarmCount}</td>
                                                        <td className="u-textRight u-textNoWrap">{money(org.totalSpentExclGst)}</td>
                                                        <td className="u-textRight u-textNoWrap">{money(org.totalDiscountExclGst)}</td>
                                                    </tr>
                                                    {isExpanded &&
                                                        purchases.map((purchase) => (
                                                            <tr key={purchase.paymentId} className="tr-nested">
                                                                <td>
                                                                    {utils.dateLong(purchase.transactionTime, "-")}
                                                                    <span className="u-ml-sm">{purchase.paidBy || "Unknown"}</span>
                                                                    <span className="u-textLight u-ml-sm">{purchaseLabel(purchase)}</span>
                                                                </td>
                                                                <td className="u-textRight u-textNoWrap">{purchase.discountPercentage}%</td>
                                                                <td></td>
                                                                <td className="u-textRight u-textNoWrap">{purchase.farmCount}</td>
                                                                <td className="u-textRight u-textNoWrap">{money(purchase.amountExclGst)}</td>
                                                                <td className="u-textRight u-textNoWrap">{money(purchase.discountAmountExclGst)}</td>
                                                            </tr>
                                                        ))}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <th>Total</th>
                                            <th></th>
                                            <th className="u-textRight u-textNoWrap">{totalPurchases}</th>
                                            <th></th>
                                            <th className="u-textRight u-textNoWrap">{money(totalSpent)}</th>
                                            <th className="u-textRight u-textNoWrap">{money(totalDiscount)}</th>
                                        </tr>
                                    </tfoot>
                                </table>
                            )}
                        </SortableTable>
                    </>
                )}
            </PanelBody>
        </Panel>
    );
}

/**
 * Keyed under "orgs" so that saving a discount in the org modal invalidates this report too.
 */
function useDiscountedOrgs() {
    return useQuery({
        queryKey: ["orgs", "discounts-report"],
        queryFn: async () => httpClient.get("admin/reports/discounts"),
        retry: false,
        refetchOnWindowFocus: false,
    });
}
